import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import {
  AccordionDetails,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { sectionNames } from 'src/components/QueryMenus/checklistQueriesData';
import { QueryMenus } from 'src/components/QueryMenus/QueryMenus';
import { SectionQueryIndication } from 'src/components/QueryMenus/sectionQueryIndication';
import { useQueryMenus } from 'src/components/QueryMenus/useQueryMenus';
import { ViewQuery } from 'src/components/QueryMenus/ViewQuery/ViewQuery';
import { sendRequest } from 'src/hooks/useFetch';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
// import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { clearMessage, setMessage } from 'src/store/Message';
import { ISIVReportMonitoring } from 'src/types/SIVReport';
import { formatInput } from 'src/util/STR';
import { useFieldTranslation } from '../../fields/useFieldTranslation';
import {
  IMonitoringMandatory,
  ISIVReportMandatory,
  ISIVReportModifiedKeys,
  ISIVReportModifiedKeysOfKeys,
  MandatoryData
} from './useSivReport';
import { setIsSaveButtonDisable } from 'src/store/sivReport';

const NOTES_MAXLENGTH = 2000;

export interface IMonitoring {
  getSivReportData: (key: ISIVReportModifiedKeysOfKeys, add: boolean) => void;
  saveDisable: boolean;
  updateMonitoring: (data: ISIVReportMonitoring) => void;
  monitoringData: ISIVReportMonitoring;
  isUpdate: boolean;
  isChange: boolean;
  setIsChange: (step: ISIVReportModifiedKeys, key: ISIVReportModifiedKeysOfKeys, val: boolean) => void;
  updateSIVReportMandatoryData: (key: keyof ISIVReportMandatory, value: MandatoryData) => void;
  mandatory: IMonitoringMandatory;
  sivReportSavedId: string;
  checkedInStartDate: any;
  isPublished: boolean;
  SignatureBreaking: () => void;
}

const Monitoring: React.FunctionComponent<IMonitoring> = ({
  getSivReportData,
  saveDisable,
  monitoringData,
  updateMonitoring,
  isUpdate,
  setIsChange,
  isChange,
  mandatory,
  updateSIVReportMandatoryData,
  sivReportSavedId,
  checkedInStartDate,
  isPublished,
  SignatureBreaking
}) => {
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const user = useUser();
  const { sivcDataUpdateSuccessMsg, sivcMendatoryInfoErrorMsg, reportMonitoring } = useFieldTranslation();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_siv_report');
  const perDisableQuery = checkPermission('view_queries_siv_report');
  const selectedRowData: any = useAppSelector(state => state.sivReport.selectedRowData);
  const saveButtonDisable: any = useAppSelector(state => state.sivReport.disableSaveButtonProps);
  const [refreshKey, setRefreshKey] = useState(0)
  const errorText = 'Required';

  useEffect(() => {
    setRefreshKey(refreshKey + 1);
  }, [monitoringData]);

  const handleMonitoringData = (e: any) => {
    dispatchApp(clearMessage());
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    const value = formattedVal;
    if (e.target.type === 'radio' && value === 'Y') {
      updateMonitoring({
        ...monitoringData,
        [e.target.name as keyof ISIVReportMonitoring]: value
      });
      updateSIVReportMandatoryData('monitoring', { ...mandatory, [e.target.name]: false });
    } else {
      updateMonitoring({
        ...monitoringData,
        [e.target.name as keyof ISIVReportMonitoring]: value
      });
      updateSIVReportMandatoryData('monitoring', { ...mandatory, [e.target.name]: false });
    }
    if(saveButtonDisable && e.target.type === 'radio') {
    dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, monitoring: false }))
    }
    setConfirmExitValue(dispatch, true);
  };

  const handleChange = () => {
    if(saveButtonDisable) {
      dispatch(setIsSaveButtonDisable(false));
    }
  }

  const setValidationData = () => {
    const copyValidation = { ...mandatory };
    for (const key of Object.keys(copyValidation)) {
      if (
        monitoringData[key as keyof ISIVReportMonitoring] === 'N' &&
        !(
          monitoringData[`${key}_comm` as keyof ISIVReportMonitoring] &&
          monitoringData[`${key}_comm` as keyof ISIVReportMonitoring] !== null &&
          monitoringData[`${key}_comm` as keyof ISIVReportMonitoring] !== ''
        )
      ) {
        copyValidation[key as keyof IMonitoringMandatory] = true;
      } else {
        copyValidation[key as keyof IMonitoringMandatory] = false;
      }
    }
    updateSIVReportMandatoryData('monitoring', copyValidation);
    return copyValidation;
  };

  const updateMonitoringData = async () => {
    const validate = setValidationData();
    if (Object.values(validate).indexOf(true) !== -1) {
      return dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
    const options = {
      site_id: selectedSite,
      study_id: studyId,
      data: {
        updated_by: user.user,
        monitoring_visit_id: selectedRowData?.id,
        siv_report: { step_two: { monitoring: monitoringData } }
      },
      pathVariables: {
        country: selectedCountry,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      if (isChange) {
        if (isUpdate) {
          if (
            monitoringData.siv_report_monitoring_plan !== null &&
            monitoringData.siv_report_monitoring_plan_comm !== null
          ) {
            await sendRequest('update_siv_report', options);
            if (isPublished) {
              SignatureBreaking();
            }
            getSivReportData('monitoring', false);
            setConfirmExitValue(dispatch, false);

            setIsChange('Step2', 'monitoring', false);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
        if (!isUpdate) {
          if (
            monitoringData.siv_report_monitoring_plan !== null &&
            monitoringData.siv_report_monitoring_plan_comm !== null
          ) {
            await sendRequest('create_siv_report', options);
            getSivReportData('monitoring', true);
            setConfirmExitValue(dispatch, false);
            setIsChange('Step2', 'monitoring', false);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
      } else if (!isChange && monitoringData.siv_report_monitoring_plan === null) {
        dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
      } else {
        dispatchApp(setMessage({ msgDetails: 'No data modified', severity: 'info' }));
      }
      dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, monitoring: true }));
    } catch (e) {
      dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
  };

  /**
   * Query menus code start
   */

  const {
    contextMenu,
    setContextMenu,
    queryKeys,
    setQueryKeys,
    checkListQueries,
    raisedQueries,
    totalFieldQueries,
    totalSectionQueries,
    fieldQueries,
    getSivReportQueries,
    report_id: reportId,
    setReport_id: setReportId,
    queryType,
    setQueryType,
    raisedSectionQueries,
    latestSecQueryRaisedBy
  } = useQueryMenus('SIV REPORT');

  useEffect(() => {
    setReportId(sivReportSavedId);
  }, [sivReportSavedId]);

  const handleContextMenu = (event: React.MouseEvent, checklistItem: keyof ISIVReportMonitoring) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null &&
        (checkPermission('view_queries_siv_report') ||
          checkPermission('view_audit_siv_report') ||
          checkPermission('raise_query_siv_report'))
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4
          }
        : null
    );

    const sectionFieldNames: string[] = [];
    Object.keys(checkListQueries.monitoring).forEach((obj: any) => {
      sectionFieldNames.push(checkListQueries.monitoring[obj].report_field_name);
    });

    const newObj = {
      ...checkListQueries.monitoring[checklistItem],
      id: sivReportSavedId,
      section_key: 'monitoring',
      report_field_value: monitoringData[checklistItem],
      section_field_names: sectionFieldNames,
      is_all_field_queries: true
    };
    setQueryKeys(newObj);
  };
  const [openViewQuery, setOpenViewQuery] = useState(false);
  const viewNotes = (checklistItem: keyof ISIVReportMonitoring, isAllFieldQueries = false) => {
    let newObj: any = {};
    if (isAllFieldQueries) {
      const sectionFieldNames: string[] = [];
      Object.keys(checkListQueries.monitoring).forEach((obj: any) => {
        sectionFieldNames.push(checkListQueries.monitoring[obj].report_field_name);
      });
      newObj = {
        ...checkListQueries.monitoring[checklistItem],
        id: sivReportSavedId,
        section_key: 'monitoring',
        section_field_names: sectionFieldNames,
        is_all_field_queries: isAllFieldQueries
      };
    } else {
      newObj = {
        ...checkListQueries.monitoring[checklistItem],
        id: sivReportSavedId,
        section_key: 'monitoring',
        report_field_value: monitoringData[checklistItem],
        is_all_field_queries: isAllFieldQueries
      };
    }

    setQueryKeys(newObj);
    setOpenViewQuery(true);
    setQueryType('ALL');
  };
  /**
   * Query menus end code
   */
  return (
    <>
      <AccordionDetails className="sq-checklist-tab">
        <Grid container className="checklist-radio-actions">
          {raisedSectionQueries.includes(sectionNames.monitoring) &&
            sectionNames.monitoring in latestSecQueryRaisedBy &&
            SectionQueryIndication(
              latestSecQueryRaisedBy,
              sectionNames.monitoring,
              viewNotes,
              'siv_report_monitoring_plan'
            )}
          <Grid md={12}>
            <Grid
              container
              onContextMenu={e => {
                handleContextMenu(e, 'siv_report_monitoring_plan');
              }}
              style={{ cursor: 'context-menu' }}
            >
              <Grid md={9}>
                {reportMonitoring.label1} <span style={{ color: 'red' }}> *</span>
              </Grid>
              <Grid md={3}>
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="siv_report_monitoring_plan"
                      value={monitoringData.siv_report_monitoring_plan}
                      onChange={handleMonitoringData}
                    >
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        disabled={!isDisable || saveDisable || !checkedInStartDate}
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <Button
                    onClick={() => viewNotes('siv_report_monitoring_plan')}
                    disabled={!perDisableQuery}
                    title="Query"
                    className={`grey-btn${
                      raisedQueries.includes('siv_report_monitoring_plan') ||
                      raisedSectionQueries.includes(sectionNames.monitoring)
                        ? ' danger-link'
                        : ''
                    }`}
                  >
                    <LiveHelpOutlinedIcon />
                  </Button>
                </Stack>
              </Grid>
              <Grid md={12} className="comment-section">
                <FormControl fullWidth margin="normal" className="siv-comment-input">
                  <TextField
                    name="siv_report_monitoring_plan_comm"
                    key={`siv_report_monitoring_plan_comm-${refreshKey}`}
                    defaultValue={monitoringData.siv_report_monitoring_plan_comm || ''}
                    onBlur={handleMonitoringData}
                    error={mandatory['siv_report_monitoring_plan']}
                    helperText={mandatory['siv_report_monitoring_plan'] ? errorText : ''}
                    label="Notes"
                    variant="outlined"
                    inputProps={{ maxLength: NOTES_MAXLENGTH }}
                    disabled={!isDisable || saveDisable || !checkedInStartDate}
                    onChange={handleChange}
                    multiline
                    minRows={1}
                    maxRows={10}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Grid container className="fixed-buttons-block">
        {checkPermission('update_siv_report') ? (
          <Grid md={12} display="flex" justifyContent="flex-end">
            <Stack spacing={2} direction="row">
              {isDisable ? (
                <Button
                  onClick={updateMonitoringData}
                  disabled={saveButtonDisable?.monitoring || !checkedInStartDate}
                  variant="text"
                >
                  SAVE
                </Button>
              ) : (
                ''
              )}
            </Stack>
          </Grid>
        ) : (
          ''
        )}
        <QueryMenus
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          queryKeys={queryKeys}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          report_id={reportId}
          checkListQueries={checkListQueries}
          queryType={queryType}
          setQueryType={setQueryType}
          isSQSivQueries
        />
        <ViewQuery
          totalFieldQueries={totalFieldQueries}
          totalSectionQueries={totalSectionQueries}
          open={openViewQuery}
          setOpen={setOpenViewQuery}
          queryKeys={queryKeys}
          queryType={queryType}
          fieldQueries={fieldQueries}
          getSivReportQueries={getSivReportQueries}
          setQueryType={setQueryType}
          report_id={reportId}
          isSQSivQueries
        />
      </Grid>
    </>
  );
};

export default Monitoring;
